import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, appId } from '../../firebase-config';
import { collection, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PRAZO_CONFIG, calcularFeriadosMoveis } from './PrazoConfig';
import { CalendarIcon, DownloadIcon, PlusIcon, TrashIcon, UploadIcon, PencilIcon } from '../Icons';

const FeriadosManager = ({ isOpen, onClose, feriados }) => {
  const userId = auth.currentUser?.uid;

  // Estados
  const [novoFeriado, setNovoFeriado] = useState({
    nome: '',
    data: '',
    tipo: PRAZO_CONFIG.TIPOS_FERIADO.ESTADUAL,
    ativo: true
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [editandoFeriado, setEditandoFeriado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  
  const handleOpenEdit = (feriado) => {
    const dataFeriado = feriado.data instanceof Date ? feriado.data : new Date(feriado.data);
    setEditandoFeriado({
        ...feriado,
        data: dataFeriado.toISOString().split('T')[0]
    });
  }
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if(editandoFeriado) {
        handleEditFeriado();
    } else {
        handleAddFeriado();
    }
  }

  // Adicionar novo feriado
  const handleAddFeriado = async () => {
    if (!novoFeriado.nome.trim() || !novoFeriado.data) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, `artifacts/${appId}/users/${userId}/feriados`), {
        ...novoFeriado,
        data: new Date(novoFeriado.data  + 'T12:00:00Z'),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setNovoFeriado({ nome: '', data: '', tipo: PRAZO_CONFIG.TIPOS_FERIADO.ESTADUAL, ativo: true });
      toast.success('Feriado adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar feriado:', error);
      toast.error('Erro ao adicionar feriado');
    } finally {
      setLoading(false);
    }
  };

  // Editar feriado
  const handleEditFeriado = async () => {
    if (!editandoFeriado || !editandoFeriado.id) return;

    setLoading(true);
    try {
      const { id, ...dataToUpdate } = editandoFeriado;
      await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/feriados`, id), {
        ...dataToUpdate,
        data: new Date(editandoFeriado.data + 'T12:00:00Z'),
        updatedAt: serverTimestamp()
      });
      setEditandoFeriado(null);
      toast.success('Feriado atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar feriado:', error);
      toast.error('Erro ao atualizar feriado');
    } finally {
      setLoading(false);
    }
  };

  const executeConfirmAction = () => {
    if (confirmAction) {
      confirmAction();
    }
    setIsConfirmOpen(false);
    setConfirmAction(null);
  };
  
  // Excluir feriado
  const handleDeleteFeriado = async (feriadoId) => {
      setConfirmAction(() => async () => {
          setLoading(true);
          try {
              await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/feriados`, feriadoId));
              toast.success('Feriado removido com sucesso!');
          } catch (error) {
              console.error('Erro ao remover feriado:', error);
              toast.error('Erro ao remover feriado');
          } finally {
              setLoading(false);
          }
      });
      setIsConfirmOpen(true);
  };
  
  // Importar feriados de um ano
  const importarFeriadosAno = async (ano) => {
    setLoading(true);
    try {
      const feriadosImportar = [
        ...PRAZO_CONFIG.FERIADOS_NACIONAIS_FIXOS.map(f => ({
          nome: f.nome,
          data: new Date(ano, f.mes - 1, f.dia),
          tipo: PRAZO_CONFIG.TIPOS_FERIADO.NACIONAL,
          ativo: true
        })),
        ...calcularFeriadosMoveis(ano).map(f => ({
          nome: f.nome,
          data: f.data,
          tipo: f.tipo,
          ativo: true
        }))
      ];
      
      const feriadosExistentes = feriados.map(f => new Date(f.data.getFullYear(), f.data.getMonth(), f.data.getDate()).getTime());
      const feriadosNovos = feriadosImportar.filter(novo => 
        !feriadosExistentes.includes(new Date(novo.data.getFullYear(), novo.data.getMonth(), novo.data.getDate()).getTime())
      );

      for (const feriado of feriadosNovos) {
        await addDoc(collection(db, `artifacts/${appId}/users/${userId}/feriados`), {
          ...feriado,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      toast.success(`${feriadosNovos.length} feriados importados para ${ano}!`);
    } catch (error) {
      console.error('Erro ao importar feriados:', error);
      toast.error('Erro ao importar feriados');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar e ordenar feriados
  const feriadosOrdenados = useMemo(() => {
    return feriados
      .filter(feriado => {
        if (!feriado.data) return false;
        const anoFeriado = feriado.data.getFullYear();
        if (anoFeriado !== filtroAno) return false;
        if (filtroTipo !== 'todos' && feriado.tipo !== filtroTipo) return false;
        return true;
      })
      .sort((a, b) => a.data - b.data);
  }, [feriados, filtroAno, filtroTipo]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-5/6 overflow-hidden">
        <div className="p-6 border-b bg-yellow-50 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <CalendarIcon className="h-8 w-8 text-yellow-600" />
                Gerenciar Feriados
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg">×</button>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
            <div className="p-6 border-r overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                {editandoFeriado ? 'Editar Feriado' : 'Adicionar Novo Feriado'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Feriado *</label>
                  <input type="text" value={editandoFeriado ? editandoFeriado.nome : novoFeriado.nome}
                    onChange={(e) => editandoFeriado ? setEditandoFeriado({...editandoFeriado, nome: e.target.value}) : setNovoFeriado({...novoFeriado, nome: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" placeholder="Ex: Aniversário da Cidade" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                  <input type="date" value={editandoFeriado ? editandoFeriado.data : novoFeriado.data}
                    onChange={(e) => editandoFeriado ? setEditandoFeriado({...editandoFeriado, data: e.target.value}) : setNovoFeriado({...novoFeriado, data: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={editandoFeriado ? editandoFeriado.tipo : novoFeriado.tipo}
                    onChange={(e) => editandoFeriado ? setEditandoFeriado({...editandoFeriado, tipo: e.target.value}) : setNovoFeriado({...novoFeriado, tipo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500">
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.ESTADUAL}>Estadual</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.MUNICIPAL}>Municipal</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.FACULTATIVO}>Facultativo</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="ativo" checked={editandoFeriado ? editandoFeriado.ativo : novoFeriado.ativo}
                    onChange={(e) => editandoFeriado ? setEditandoFeriado({...editandoFeriado, ativo: e.target.checked}) : setNovoFeriado({...novoFeriado, ativo: e.target.checked})}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"/>
                  <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">Feriado ativo</label>
                </div>
                <div className="flex gap-2 pt-4">
                  <button type="submit" disabled={loading} className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2">
                    {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <PlusIcon className="h-4 w-4" />}
                    {editandoFeriado ? 'Atualizar' : 'Adicionar'}
                  </button>
                  {editandoFeriado && (<button type="button" onClick={() => setEditandoFeriado(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>)}
                </div>
              </form>
              <div className="mt-6 pt-6 border-t space-y-4">
                <h4 className="font-semibold text-gray-700">Ferramentas</h4>
                <div className="space-y-2">
                  <button onClick={() => importarFeriadosAno(filtroAno)} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
                    <DownloadIcon className="h-4 w-4" /> Importar Feriados Nacionais de {filtroAno}
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="mb-4 space-y-2">
                <div className="flex gap-2">
                  <select value={filtroAno} onChange={(e) => setFiltroAno(parseInt(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {Array.from({length: 5}, (_, i) => { const ano = new Date().getFullYear() + i - 2; return (<option key={ano} value={ano}>{ano}</option>); })}
                  </select>
                  <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="todos">Todos os tipos</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.NACIONAL}>Nacional</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.ESTADUAL}>Estadual</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.MUNICIPAL}>Municipal</option>
                    <option value={PRAZO_CONFIG.TIPOS_FERIADO.FACULTATIVO}>Facultativo</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">{feriadosOrdenados.length} feriados em {filtroAno}</div>
              </div>
              <div className="space-y-2">
                {feriadosOrdenados.map((feriado) => (
                    <div key={feriado.id} className={`p-3 rounded-lg border ${feriado.ativo ? 'bg-white' : 'bg-gray-50 opacity-60'} hover:bg-gray-50`}>
                        <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <div className="font-medium text-gray-800">{feriado.nome}</div>
                            {feriado.data && <div className="text-sm text-gray-600">{feriado.data.toLocaleDateString('pt-BR', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</div>}
                            <div className="text-xs text-gray-500 mt-1"><span className={`inline-block px-2 py-1 rounded text-xs ${ feriado.tipo === PRAZO_CONFIG.TIPOS_FERIADO.NACIONAL ? 'bg-blue-100 text-blue-800' : feriado.tipo === PRAZO_CONFIG.TIPOS_FERIADO.ESTADUAL ? 'bg-green-100 text-green-800' : feriado.tipo === PRAZO_CONFIG.TIPOS_FERIADO.MUNICIPAL ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800' }`}>{feriado.tipo.charAt(0).toUpperCase() + feriado.tipo.slice(1)}</span></div>
                        </div>
                        <div className="flex gap-1 ml-2">
                            <button onClick={() => handleOpenEdit(feriado)} className="p-1 text-gray-400 hover:text-blue-600"><PencilIcon className="h-4 w-4" /></button>
                            <button onClick={() => handleDeleteFeriado(feriado.id)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                        </div>
                        </div>
                    </div>
                ))}
                {feriadosOrdenados.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p>Nenhum feriado encontrado para {filtroAno}</p>
                    <p className="text-sm mt-2">Use o formulário para adicionar ou importe os feriados nacionais.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isConfirmOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Confirmar Ação</h3>
                <p className="text-gray-600 mb-6">Tem certeza que deseja realizar esta ação?</p>
                <div className="flex justify-end gap-3">
                <button onClick={() => setIsConfirmOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancelar</button>
                <button onClick={executeConfirmAction} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Confirmar</button>
                </div>
            </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default FeriadosManager;